/**
 * @file MemberRoutineCards.jsx
 * @location src/components/tasks/MemberRoutineCards.jsx
 * @role 팀원용 루틴 카드 그리드 — 매니저 RecurringGroups 와 같은 카드, 단 도넛(전체 진척) 대신
 *   본인 현재 발생분의 체크(Chk). 클릭=본인 완료 토글. 추가·편집·정지 없음(루틴은 매니저 전용 — razor ㉒).
 */
import { useMemo } from 'react';
import { useApp } from '../../store/AppContext';
import { getTodayStr } from '../../data/constants';
import { recurrenceLabel, recurrenceFreqMark } from '../../utils/recurrenceLabels';
import { Chk, EmptyState } from '../ui/SharedUI';

/**
 * @param {object} props
 * @param {import('../../data/types.js').TaskTemplate[]} props.templates
 * @param {(id: string) => void} props.handleToggle
 */
export function MemberRoutineCards({ templates, handleToggle }) {
  const { S } = useApp();
  const today = getTodayStr();
  const mine = useMemo(
    () => (templates ?? []).filter(t => t.active && (t.aees ?? []).includes(S.uid)),
    [templates, S.uid],
  );
  // 본인 루틴 발생분을 templateId별 최신(occ≤today·중지 제외)으로 1회 그룹핑 → 카드별 O(1) 조회
  const latestByTemplate = useMemo(() => {
    const map = new Map();
    for (const t of S.tasks) {
      if (t.aee !== S.uid || !t.templateId || !t.occurrenceDate) continue;
      if (t.occurrenceDate > today || t.status === 'cancelled') continue;
      const cur = map.get(t.templateId);
      if (!cur || t.occurrenceDate > cur.occurrenceDate) map.set(t.templateId, t);
    }
    return map;
  }, [S.tasks, S.uid, today]);

  if (!mine.length) {
    return <div className="panel"><EmptyState icon="reset" title="담당 루틴 업무가 없습니다" desc="매니저가 할당하면 여기에 표시됩니다" /></div>;
  }

  return (
    <div className="routine-card-grid">
      {mine.map(t => {
        const task = latestByTemplate.get(t.id) ?? null;
        return (
          <div key={t.id} className="routine-card routine-card-static">
            <div className="routine-card-meta">
              <span className="routine-card-freq">
                <span className="routine-card-freq-mark">{recurrenceFreqMark(t.repeat)}</span>
                <span className="routine-card-freq-label">{recurrenceLabel(t.repeat)}</span>
              </span>
            </div>
            <div className="routine-card-title">{t.title}</div>
            <div className="routine-card-ring">
              {task
                ? <Chk done={task.status === 'done'} onClick={() => handleToggle(task.id)} />
                : <span className="routine-card-count empty">—</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
